import styled from 'styled-components';
import Head from 'next/head';
import {
  trackAnalytics,
  trackAnalyticsNew,
  attachLocale,
  makeExternalUrl,
  attachSubcategorySlug
} from '../../../../lib/utils';
import { DATA_TRACK_TYPE_INTERNAL, DOM_CATEGORY_DIRECTORY } from '../../../../lib/constants';

const CategoryGroupHeaderDiv = styled.div`
  margin-top: ${(p) => p.theme.unit(8)};
  margin-bottom: ${(p) => p.theme.unit(4)};

  display: flex;
  align-items: center;
`;

const CategoryGroupDiv = styled.div`
  border-bottom: ${(p) => (p.isLastChild ? 'none' : `1px solid ${p.theme.c.border.primary}`)};
`;

const CategoryIconImg = styled.img`
  width: ${(p) => p.theme.unit(4)};
  height: ${(p) => p.theme.unit(4)};
  margin-right: ${(p) => p.theme.unit(2)};;
`;

const CategoryName = styled.h3`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  text-align: left;
  color: ${(p) => p.theme.c.text.primary};
  position: relative;
  display: inline;
  margin: 0;
`;

const SubCategoryAnchor = styled.a`
  ${({ theme }) => theme.typographyRebrand.body.lg}
  text-align: left;
  color: ${(p) => p.theme.c.text.interactive};
  text-decoration: none;
  margin-right: ${(p) => p.theme.unit(4)};
  :hover {
    color: ${({ theme }) => theme.c.text['interactive-hover']};
  }
`;

const SubCategoryDiv = styled.div`
  margin-bottom: ${(p) => p.theme.unit(8)};
  ${(p) => (p.isMobile
    ? `
      display: flex;
      flex-direction: column;
      gap: 16px;
    `
    : '')};
`;

const DividerDiv = styled.div`
  width: 100%;
  border-bottom: 1px solid ${(p) => p.theme.c.border.primary};
`;

const CategoryGroup = (props) => {
  const {
    category,
    index,
    length,
    isMobile,
    locale,
    globalPageConfig,
  } = props;

  const categoryIcon = category && category.categoryIconUrl ? category.categoryIconUrl : '';
  const categoryIconUrl = makeExternalUrl(categoryIcon);
  const categoryDirectoryParentCategoryPrefix = globalPageConfig.categoryDirectoryParentCategoryPrefix || 'All';

  const parentCategorySlug = category && category.slug ? category.slug : '';
  const parentCategoryAnalyticsSlug = category.analyticsSlug || parentCategorySlug;
  const trackingLocation = `page:categories:${parentCategoryAnalyticsSlug}`;
  // Sub category links
  let subCategories = category.subCategories || [];
  subCategories = subCategories.map((subCategory) => {
    const {
      analyticsSlug,
      tags = {},
      slug = '',
    } = subCategory;
    const subCategoryAnalyticsSlug = analyticsSlug || slug;

    const resolvedSubCategoryUrl = attachSubcategorySlug(parentCategorySlug, slug);
    const resolvedSlug = attachLocale(resolvedSubCategoryUrl, locale);
    const trackingSlug = `${parentCategorySlug}_${slug}`;
    const legacyTrack = trackAnalytics(DOM_CATEGORY_DIRECTORY, trackingSlug, resolvedSlug, tags);
    const track = trackAnalyticsNew({
      url: legacyTrack.url,
      location: trackingLocation,
      name: subCategoryAnalyticsSlug,
    });

    return (
      // check if the name start with space so that we don't display placeholder subcategories and show empty link
      !subCategory.name.startsWith(' ') ? (
        <SubCategoryAnchor
          isMobile={isMobile}
          key={`sub-category-anchor-${subCategory.slug}`}
          href={track.url}
          data-track-id={track.dataTrackingId}
          data-track-click={track.dataTrack}
          data-track-type={DATA_TRACK_TYPE_INTERNAL}
      >
          {subCategory.name}
        </SubCategoryAnchor>
      ) : null
    );
  });

  // Generate Link for parent category and place it as the first link
  const resolvedLocaleLink = attachLocale(`categories/${category.slug}`, locale);
  const legacyTrack = trackAnalytics(
    DOM_CATEGORY_DIRECTORY,
    `CategoryGroupSubCategory-${category.slug}`,
    resolvedLocaleLink,
    category.tags
  );
  const track = trackAnalyticsNew({
    url: legacyTrack.url,
    location: trackingLocation,
    name: `all-${parentCategoryAnalyticsSlug}`,
  });

  const parentCategory = (
    <SubCategoryAnchor
      isMobile={isMobile}
      key={`sub-category-anchor-${category.slug}`}
      href={track.url}
      data-track-id={track.dataTrackingId}
      data-track-click={track.dataTrack}
      data-track-type={DATA_TRACK_TYPE_INTERNAL}
    >
      {categoryDirectoryParentCategoryPrefix}
      {' '}
      {category.name}
    </SubCategoryAnchor>
  );
  subCategories = [parentCategory, ...subCategories];

  const isLastChild = (index === length - 1);

  return (
    <CategoryGroupDiv isLastChild={isLastChild}>
      <CategoryGroupHeaderDiv>
        <CategoryIconImg src={categoryIconUrl} alt="" />
        <CategoryName>{category.name}</CategoryName>
      </CategoryGroupHeaderDiv>
      <SubCategoryDiv isMobile={isMobile}>
        {subCategories}
      </SubCategoryDiv>
    </CategoryGroupDiv>
  );
};

const CategoriesDirectoryContent = (props) => {
  const {
    allCategories,
    globalPageConfig,
    globalCategoryConfig,
    locale,
    isMobile,
  } = props;

  const {
    metaDescription = '',
    directoryPageTitle = '',
  } = globalCategoryConfig;

  const metaDescriptionToUse = metaDescription || (globalPageConfig && globalPageConfig.defaultMetaDescription);
  const pageTitleBase = globalPageConfig && globalPageConfig.pageTitleBase ? globalPageConfig.pageTitleBase : '';
  const pageTitleToUse = `${directoryPageTitle} ${pageTitleBase}`;

  const categories = allCategories || [];

  const Categories = () => {
    return categories.map((category, index, self) => {
      return (
        <CategoryGroup
          key={`category-group-${category.name}`}
          category={category}
          locale={locale}
          index={index}
          length={self.length}
          isMobile={isMobile}
          globalPageConfig={globalPageConfig}
        />
      );
    });
  };

  return (
    <div>
      <Head>
        <title>{pageTitleToUse}</title>
        <meta name="description" content={metaDescriptionToUse} />
      </Head>

      <DividerDiv />
      {Categories()}
    </div>
  );
};

export default CategoriesDirectoryContent;
